import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Divider, Panel } from 'rsuite';
import { useActiveTimeOffSource } from '../hooks/useQueries';
import SearchLauncher from '../components/SearchLauncher';
import usePermissions, { canCreateProjects, canOpenUserManagement } from '../hooks/usePermissions';

import './ConfigurationPage.css';

const configCards = [
  {
    key: 'integrations',
    title: 'Integrations',
    description: 'Configure your active hours source and manage close date integrations.',
    iconClass: 'bi bi-plug-fill',
    path: '/configuration/integrations',
  },
  {
    key: 'users',
    title: 'User Management',
    description: 'Maintain users, reserved hours, and project rate schedules.',
    iconClass: 'bi bi-people-fill',
    path: '/configuration/users',
  },
  {
    key: 'projects',
    title: 'Project Management',
    description: 'Review archived projects and recover projects from trash.',
    iconClass: 'bi bi-kanban-fill',
    path: '/configuration/projects',
  },
];

export default function ConfigurationHomePage() {
  const navigate = useNavigate();
  const permissions = usePermissions();
  const { data: activeTimeOffSource } = useActiveTimeOffSource();
  const activeTimeOffSourceName = String(activeTimeOffSource?.name || 'manual').toLowerCase();
  const showTimeOffManager = activeTimeOffSourceName === 'manual';

  const cards = showTimeOffManager
    ? [
        ...configCards,
        {
          key: 'time-off',
          title: 'Time Off Manager',
          description: 'Create and manage user time off ranges with editable hours.',
          iconClass: 'bi bi-calendar2-week-fill',
          path: '/configuration/time-off',
        },
      ]
    : configCards;

  const settingsSearchItems = useMemo(
    () => [
      {
        id: 'integrations',
        title: 'Integrations',
        description: 'Configure hours provider, time off source, and close dates.',
        destination: 'Integrations',
        route: '/configuration/integrations',
        iconClass: 'bi bi-plug-fill',
        keywords: ['integrations', 'hours', 'provider', 'timecamp', 'time off', 'close dates', 'holidays'],
      },
      {
        id: 'hours-provider',
        title: 'Hours Provider',
        description: 'Choose and configure your active hours source, including TimeCamp.',
        destination: 'Integrations',
        route: '/configuration/integrations',
        iconClass: 'bi bi-clock-history',
        keywords: ['hours', 'provider', 'timecamp', 'manual', 'timesheets'],
      },
      {
        id: 'time-off-source',
        title: 'Time Off Source',
        description: 'Set where PTO and leave data come from.',
        destination: 'Integrations',
        route: '/configuration/integrations',
        iconClass: 'bi bi-calendar2-check',
        keywords: ['time off', 'pto', 'leave', 'vacation', 'source'],
      },
      {
        id: 'close-dates',
        title: 'Holidays / Close Dates',
        description: 'Manage holiday close dates and source settings.',
        destination: 'Integrations',
        route: '/configuration/integrations',
        iconClass: 'bi bi-calendar-event',
        keywords: ['holidays', 'holiday', 'close dates', 'employeeconnect', 'calendar'],
      },
      {
        id: 'users',
        title: 'User Management',
        description: 'Maintain users, reserved hours, and project rate schedules.',
        destination: 'User Management',
        route: '/configuration/users',
        iconClass: 'bi bi-people-fill',
        keywords: ['users', 'employees', 'team', 'reserved hours', 'profiles'],
      },
      {
        id: 'add-employee',
        title: 'Add Employee',
        description: 'Go to user management to create a new user.',
        destination: 'User Management',
        route: '/configuration/users',
        iconClass: 'bi bi-person-plus-fill',
        keywords: ['add employee', 'add user', 'new user', 'hire'],
      },
      {
        id: 'rates',
        title: 'Rates',
        description: 'View current project rates and edit per user.',
        destination: 'User Management',
        route: '/configuration/users',
        iconClass: 'bi bi-currency-dollar',
        keywords: ['rates', 'billing', 'cost rate', 'hourly'],
      },
      {
        id: 'add-rate',
        title: 'Add Rate',
        description: 'Go to rates and add a new effective dated rate.',
        destination: 'User Management',
        route: '/configuration/users',
        iconClass: 'bi bi-plus-square-fill',
        keywords: ['add rate', 'new rate', 'rate change'],
      },
      {
        id: 'escalate-rates',
        title: 'Escalate Rates',
        description: 'Bulk increase current rates for selected users.',
        destination: 'User Management',
        route: '/configuration/users',
        iconClass: 'bi bi-arrow-up-right-square-fill',
        keywords: ['escalate', 'rate escalation', 'increase rates', 'uplift'],
      },
      {
        id: 'projects',
        title: 'Project Management',
        description: 'Review archived projects and recover projects from trash.',
        destination: 'Project Management',
        route: '/configuration/projects',
        iconClass: 'bi bi-kanban-fill',
        keywords: ['projects', 'project settings', 'management'],
      },
      {
        id: 'archive-projects',
        title: 'Archived Projects',
        description: 'Review archived projects and unarchive as needed.',
        destination: 'Project Management',
        route: '/configuration/projects',
        iconClass: 'bi bi-archive-fill',
        keywords: ['archive', 'archived', 'unarchive'],
      },
      {
        id: 'project-trash',
        title: 'Project Trash',
        description: 'Restore or permanently delete trashed projects.',
        destination: 'Project Management',
        route: '/configuration/projects',
        iconClass: 'bi bi-trash-fill',
        keywords: ['trash', 'deleted', 'restore', 'permanent delete'],
      },
      {
        id: 'time-off-manager',
        title: 'Time Off Manager',
        description: 'Manage user time off ranges with editable hours.',
        destination: 'Time Off Manager',
        route: '/configuration/time-off',
        iconClass: 'bi bi-calendar2-week-fill',
        keywords: ['time off manager', 'time off', 'pto', 'vacation', 'leave'],
        hidden: !showTimeOffManager,
      },
    ].filter((item) => !item.hidden),
    [showTimeOffManager]
  );

  // The same gates as the routes in App.js.
  const sectionAllowed = {
    '/configuration/integrations': permissions.can('integrations.manage'),
    '/configuration/users': canOpenUserManagement(permissions),
    '/configuration/projects': canCreateProjects(permissions),
  };
  const canOpen = (path) => sectionAllowed[path] ?? true;

  const selectSettingsSearchResult = (item) => {
    const targetRoute = String(item?.route || '').trim();
    if (!targetRoute) return;
    navigate(targetRoute);
  };

  return (
    <div className="ppc-page">
      <div className="ppc-config-home-header-row">
        <h2 className="ppc-config-home-title">
          <span className="ppc-config-home-title__row">
            <i className="bi bi-gear" aria-hidden="true" />
            <span>Configuration</span>
          </span>
        </h2>
      </div>

      <SearchLauncher
        idPrefix="settings-search"
        label="Find a setting"
        placeholder="Search settings: rates, holidays, users, hours, projects, time off, timecamp, escalate..."
        resultsLabel="Settings search results"
        emptyText="No settings matched your search."
        items={settingsSearchItems.filter((item) => canOpen(item.route))}
        onSelect={selectSettingsSearchResult}
      />

      <Divider />

      <Panel bordered>
        <div className="ppc-config-nav-grid">
          {cards.filter((card) => canOpen(card.path)).map((card) => (
            <button
              key={card.key}
              type="button"
              className="ppc-config-nav-card"
              onClick={() => navigate(card.path)}
            >
              <div className="ppc-config-nav-card__icon" aria-hidden="true">
                <i className={card.iconClass} />
              </div>
              <div className="ppc-config-nav-card__content">
                <div className="ppc-config-nav-card__title">{card.title}</div>
                <div className="ppc-config-nav-card__description">{card.description}</div>
              </div>
              <div className="ppc-config-nav-card__chevron" aria-hidden="true">
                <i className="bi bi-arrow-right" />
              </div>
            </button>
          ))}
        </div>
      </Panel>
    </div>
  );
}
